import { TFunction } from "i18next";
import { debounce, isString } from "lodash";
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import { useFormatCurrency, useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import Button from "@krea/customer-features/shared-components/button";
import TextInput from "@krea/customer-features/shared-components/fields/input";
import Radio from "@krea/customer-features/shared-components/fields/radio";
import RadioGroup from "@krea/customer-features/shared-components/fields/radiogroup";
import { Helper } from "@krea/customer-features/shared-components/helper";
import { Text } from "@krea/customer-features/shared-components/text";
import {
  useIsFieldRequestSubmitting,
  usePostFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import {
  useGetFieldRequests,
  useGetOneFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/queries";
import { FormatNumber, ScrollToElement } from "@krea/customer-features/utils";
import twMerge from "@krea/customer-features/utils/tailwind-utils";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";
import { FieldRequestForm } from "../GetFieldRequestForm";

import { INIT_TYPE } from "./AccountingDataForm";

enum VERIFICATION_FR_TYPES {
  ACCOUNTING_DATA = "accountingData",
  BANKING_DATA = "bankingData",
  // DELEGATE = "delegate",
}

const getVerificationOptions = (t: TFunction) => {
  return [
    {
      id: VERIFICATION_FR_TYPES.BANKING_DATA,
      title: t(
        "iaq.selfEstimatedHistoricRevenue.verificationOptions.bankingData.title",
      ),
      text: t("iaq.selfEstimatedHistoricRevenue.automaticFetching"),
      initType: null,
    },
    {
      id: `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FILES}`,
      title: t(
        "iaq.selfEstimatedHistoricRevenue.verificationOptions.accountingFiles.title",
      ),
      initType: INIT_TYPE.FILES,
    },
    // {
    //   id: VERIFICATION_FR_TYPES.DELEGATE,
    //   title: t("iaq.accountingData.delegateChoice"),

    //   initType: null,
    // },
  ];
};

const getAccountingAndBankingDataFRIDs = (fieldRequests: any) => {
  // TODO: type
  const accountingDataFieldRequestID = fieldRequests.find(
    ({ field }: { field: VERIFICATION_FR_TYPES }) => {
      return field === VERIFICATION_FR_TYPES.ACCOUNTING_DATA;
    },
  )?.id;

  const bankingDataFieldRequestID = fieldRequests.find(
    ({ field }: { field: VERIFICATION_FR_TYPES }) => {
      return field === VERIFICATION_FR_TYPES.BANKING_DATA;
    },
  )?.id;

  return { accountingDataFieldRequestID, bankingDataFieldRequestID };
};

const SelfEstimatedHistoricRevenue = ({
  fieldRequestID,
  withInputLabel,
  isPreviewMode,
}: {
  fieldRequestID: string;
  withInputLabel: boolean;
  isPreviewMode: boolean;
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const [showVerificationOptions, setShowVerificationOptions] = useState(false);
  const [selectedVerificationOptionID, setSelectedVerificationOptionID] =
    useState<string | null>(null);

  const { data: allFieldRequests = [] } = useGetFieldRequests();
  const { accountingDataFieldRequestID, bankingDataFieldRequestID } =
    getAccountingAndBankingDataFRIDs(allFieldRequests);
  const verificationOptions = getVerificationOptions(t);

  const { data: currentFieldRequest } = useGetOneFieldRequest<string | null>(
    fieldRequestID,
  );

  const { data: accountingDataFieldRequest } = useGetOneFieldRequest(
    accountingDataFieldRequestID,
  );
  const { data: bankingDataFieldRequest } = useGetOneFieldRequest(
    bankingDataFieldRequestID,
  );

  const { mutate: postFieldRequest, error: err } =
    usePostFieldRequest(fieldRequestID);

  const error = (err as any)?.response?.data?.errors?.[0];

  const isCurrentFRSubmitting = useIsFieldRequestSubmitting(fieldRequestID);
  const isAccountingDataFRSubmitting = useIsFieldRequestSubmitting(
    accountingDataFieldRequestID,
  );
  const isBankingDataFRSubmitting = useIsFieldRequestSubmitting(
    bankingDataFieldRequestID,
  );

  const [touched, setTouched] = useState(false);

  const [value, setValue] = useState(
    currentFieldRequest?.submittedData
      ? parseInt(currentFieldRequest.submittedData)
      : null,
  );

  const isAnyFRSubmitting =
    isCurrentFRSubmitting ||
    isAccountingDataFRSubmitting ||
    isBankingDataFRSubmitting;

  const hasAnySubmittedVerificationFR =
    accountingDataFieldRequest?.isSubmitted ??
    bankingDataFieldRequest?.isSubmitted;
  const isAnySubmitted =
    currentFieldRequest?.isSubmitted &&
    (hasAnySubmittedVerificationFR ?? !showVerificationOptions);

  const onPostValueFn = (newValue: number | null) => {
    setTouched(false);
    postFieldRequest({ value: newValue });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onPostValueFn(value);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((newValue: number | null) => onPostValueFn(newValue), 500),
    [],
  );

  const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInt(target.value.replace(/\s+/g, ""), 10);
    const newValue = Number.isInteger(parsedValue) ? parsedValue : null;

    setTouched(true);
    setValue(newValue);
    debouncedSave(newValue);
  };

  const onBlur = () => {
    if (touched) {
      debouncedSave.cancel();
      onPostValueFn(value);
    }
  };

  const openVerificationOptions = () => {
    setShowVerificationOptions(!showVerificationOptions);

    if (showVerificationOptions) {
      setSelectedVerificationOptionID(null);
    } else if (accountingDataFieldRequest?.isSubmitted) {
      const { erp_system } = accountingDataFieldRequest.submittedData as {
        erp_system: string;
      };

      if (erp_system === "OTHER") {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FILES}`,
        );
      } else if (erp_system === "fortnox") {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FORTNOX}`,
        );
      } else if (erp_system === "visma") {
        setSelectedVerificationOptionID(
          `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.VISMA_E_ECONOMY}`,
        );
      }
    } else if (bankingDataFieldRequest?.isSubmitted) {
      setSelectedVerificationOptionID(VERIFICATION_FR_TYPES.BANKING_DATA);
    }
  };

  useEffect(() => {
    if (!currentFieldRequest?.isSubmitted) {
      setShowVerificationOptions(false);
      setSelectedVerificationOptionID(null);
    }
  }, [currentFieldRequest?.isSubmitted]);

  return isPreviewMode ? (
    <FieldRequestPreviewHeader
      label={currentFieldRequest?.displayName}
      value={formattedCurrencyValue(value)}
      className=""
    />
  ) : (
    <>
      <form
        className={twMerge("input-form", {
          "input-form-padded": isPreviewMode,
        })}
        onKeyDown={onKeyDown}
        onSubmit={(e) => e.preventDefault()}
      >
        <TextInput
          type="text"
          pattern="[0-9]*"
          maxLength={15}
          inputMode="numeric"
          id={`input_${currentFieldRequest?.field}`}
          value={FormatNumber(value)}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={t("iaq.amount.valuePlaceholder")!}
          textAppend={currencyValue}
          label={withInputLabel && currentFieldRequest?.displayName}
          error={error}
        />

        <FieldRequestSaved
          fieldRequestID={fieldRequestID}
          isSubmitting={!!isAnyFRSubmitting}
          isSubmitted={isAnySubmitted}
        />
      </form>

      {currentFieldRequest?.isSubmitted && (
        <div
          className={
            "tw-mt-8 tw-justify-start tw-rounded tw-border tw-border-purple-50 tw-px-4 tw-py-1"
          }
        >
          <div
            className={
              "tw-flex tw-w-full tw-items-center tw-justify-between tw-p-1"
            }
          >
            <Text
              className={
                "tw-mr-3 tw-flex tw-flex-row tw-flex-wrap tw-whitespace-pre-wrap tw-align-middle tw-font-bold sm:tw-mr-0"
              }
            >
              <Trans
                i18nKey="iaq.selfEstimatedHistoricRevenue.verifyRevenueTitle"
                components={{
                  span: <span />,
                  helper: (
                    <Helper
                      className={"tw--mt-1 tw-ml-1"}
                      content={t("iaq.selfEstimatedHistoricRevenue.helperText")}
                      useQuestionIcon={true}
                    />
                  ),
                }}
              />
            </Text>

            <Button
              variant="text"
              size="sm"
              onClick={openVerificationOptions}
              disabled={!!isCurrentFRSubmitting}
            >
              {showVerificationOptions
                ? t("iaq.close")
                : t("iaq.selfEstimatedHistoricRevenue.verify")}
            </Button>
          </div>

          {showVerificationOptions && (
            <RadioGroup
              id={selectedVerificationOptionID!}
              className={"tw-w-full tw-py-3"}
            >
              {verificationOptions.map(({ id, title, text, initType }) => {
                const verificationFieldRequestID =
                  id === VERIFICATION_FR_TYPES.BANKING_DATA
                    ? bankingDataFieldRequestID
                    : id ===
                        `${VERIFICATION_FR_TYPES.ACCOUNTING_DATA}-${INIT_TYPE.FILES}`
                      ? accountingDataFieldRequestID
                      : null;

                return (
                  <div
                    key={id}
                    className={twMerge(
                      "tw-border tw-border-solid tw-rounded",
                      selectedVerificationOptionID === id
                        ? "tw-border-purple-300 tw-bg-purple-50"
                        : "tw-border-purple-50",
                    )}
                  >
                    <Radio
                      id={id}
                      value={id}
                      name={id}
                      disabled={false}
                      error={null}
                      onClick={({ target }) => {
                        if (isMobile) {
                          ScrollToElement(
                            "fieldRequestForm",
                            undefined,
                            undefined,
                          );
                        }
                        setSelectedVerificationOptionID(
                          (target as HTMLInputElement).value,
                        );
                      }}
                      checked={selectedVerificationOptionID === id}
                      readOnly={true}
                      className={"tw-px-5 tw-py-2"}
                      iconClassName={"tw-mt-[8.5px] tw-ml-[10px]"}
                      label={
                        <div className={"tw-ml-3 tw-text-primary"}>
                          {title}{" "}
                          {text && (
                            <span className="tw-font-normal">- {text}</span>
                          )}
                        </div>
                      }
                    />

                    {selectedVerificationOptionID === id && (
                      <div
                        id="fieldRequestForm"
                        className={twMerge("tw-bg-white tw-rounded tw-px-4", {
                          "tw-pt-4 tw-pb-4":
                            id !== VERIFICATION_FR_TYPES.BANKING_DATA,
                        })}
                      >
                        {/* {id === VERIFICATION_FR_TYPES.DELEGATE ? (
                            <FieldRequestDelegateForm
                              fieldRequestID={fieldRequestID}
                            />
                          ) : ( */}
                        <FieldRequestForm
                          fieldRequestID={verificationFieldRequestID}
                          initType={initType}
                          hasParentFieldRequest={true}
                        />
                        {/* )} */}
                      </div>
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          )}
        </div>
      )}
    </>
  );
};

export default SelfEstimatedHistoricRevenue;
